import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ProfileHeader,
  InfoCard,
  NotificationPreferences,
  SecuritySettings,
  SettingsSection,
  LogoutButton,
} from './components';
import {
  CustomerProfile as CustomerProfileType,
  BusinessInfo,
  ContactInfo,
  NotificationPreference,
  defaultNotificationPreferences,
  defaultSecurityOptions,
  defaultSettingsItems,
} from './profileTypes';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { ChangePasswordModal } from '../../../components/ChangePasswordModal';
import { getMyCustomerProfile, Customer, mapTierLabel } from '../../../services/customersApi';
import styles from './CustomerProfile.module.css';

function adapt(c: Customer): {
  profile: CustomerProfileType;
  business: BusinessInfo;
  contact: ContactInfo;
} {
  const businessName = c.store_name || c.full_name || 'N/A';
  const fullAddress = [c.address_line1, c.city, c.state, c.zip_code]
    .filter(Boolean)
    .join(', ') || 'N/A';
  const tier = mapTierLabel(c.pricing_tier_name);
  const creditMap: Record<typeof tier, string> = {
    Gold: '$25,000',
    Silver: '$15,000',
    Bronze: '$5,000',
  };
  return {
    profile: {
      id: String(c.id),
      businessName,
      contactPerson: c.full_name || 'N/A',
      // Note: Customer (business-account) records don't carry the backend's
      // account_status field (that's users-table only) — treat missing/undefined
      // is_active as active rather than inactive, matching the auth User pattern.
      status: c.is_active !== false ? 'Active' : 'Inactive',
      creditLimit: creditMap[tier],
      paymentTerms: 'Net 30',
    },
    business: {
      businessName,
      businessAddress: fullAddress,
      taxId: c.llc_name || 'N/A',
      businessLicense: 'View Document',
    },
    contact: {
      contactPerson: c.full_name || 'N/A',
      email: c.email || 'N/A',
      phone: c.contact_phone || 'N/A',
      deliveryAddress: fullAddress,
    },
  };
}

const CustomerProfile: React.FC = () => {
  const [data, setData] = useState<{ profile: CustomerProfileType; business: BusinessInfo; contact: ContactInfo } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<NotificationPreference[]>(defaultNotificationPreferences);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const handleSecurityClick = useCallback((id: string) => {
    if (id === 'change-password') setShowPasswordModal(true);
  }, []);

  const load = () => {
    setLoading(true);
    setError(null);
    getMyCustomerProfile()
      .then((c) => setData(adapt(c)))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load profile'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleTogglePreference = useCallback((id: string) => {
    setPreferences((prev) => prev.map((p) => (p.id === id ? { ...p, enabled: !p.enabled } : p)));
  }, []);

  const businessFields = useMemo(() => data ? [
    { label: 'Business Name', value: data.business.businessName },
    { label: 'Business Address', value: data.business.businessAddress },
    { label: 'Tax ID', value: data.business.taxId },
    { label: 'Business License', value: data.business.businessLicense, isLink: true, linkIcon: true },
  ] : [], [data]);

  const contactFields = useMemo(() => data ? [
    { label: 'Contact Person', value: data.contact.contactPerson },
    { label: 'Email', value: data.contact.email },
    { label: 'Phone', value: data.contact.phone },
    { label: 'Delivery Address', value: data.contact.deliveryAddress },
  ] : [], [data]);

  return (
    <div className={styles.container}>
      <AsyncBoundary
        loading={loading && !data}
        error={error}
        empty={!loading && !data}
        emptyMessage="Profile unavailable"
        onRetry={load}
      >
        {data && (
          <>
            <ProfileHeader profile={data.profile} />
            <div className={styles.infoGrid}>
              <InfoCard title="Business Information" icon="business" fields={businessFields} />
              <InfoCard title="Contact Information" icon="contact" fields={contactFields} />
            </div>
            <NotificationPreferences preferences={preferences} onToggle={handleTogglePreference} />
            <SecuritySettings options={defaultSecurityOptions} onOptionClick={handleSecurityClick} />
            <SettingsSection items={defaultSettingsItems} />
            <LogoutButton />
            <p className={styles.version}>VIN pulse OS v2.1.0</p>
          </>
        )}
      </AsyncBoundary>

      <ChangePasswordModal
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
    </div>
  );
};

export const CustomerProfilePage: React.FC = () => {
  return <CustomerProfile />;
};

export { CustomerProfile };
export default CustomerProfile;
